// MARK://dhk8/admin-shell/components/BrandMark
// Shared brand mark used by the auth surfaces (Login/Signup/Onboarding) and the
// shell header (AdminLayout). The glyph is the CANONICAL NintaiGO "grain/bamboo"
// mark — the exact same artwork the guest PWA ships as its favicon
// (`apps/dhk8-guest/public/favicon.svg` / `packages/branding/nintaigo/favicon.svg`).
// Keeping one glyph means the admin, the POS, the guest PWA and the marketing
// site all read as a single brand at first glance.
//
// The 10 white paths in `GRAIN_GLYPH` are lifted verbatim from that favicon
// (same 32×32 viewBox + `translate(3 3) scale(0.024074)` transform group). The
// favicon's own green `<rect>` background is intentionally dropped: the green
// gradient + rounded corners come from the `.dhk8-brandmark__glyph` span
// (`--brand-grad-leaf`, base `#1F8B3A`).
//
// The implementation is plain CSS variables + inline SVG: no extra deps, no
// emojis (per UI plan), no rounded "shadowed" surfaces past the logo itself.
// Tokens consumed:
//   --brand-grad        green leaf gradient (defined in nintaigo-brand.css)
//   --brand-ink         primary text color
//   --brand-ink-accent  "GO" tail color
//   --font-display      Inter Tight stack (set by @dockethub/ui theme)
//
// Three sizes: `sm` (24px glyph, tight chrome / dialogs), `md` (32px glyph,
// header use) and `lg` (48px glyph, auth use).

interface BrandMarkProps {
  size?: 'sm' | 'md' | 'lg'
  /** When true, hides the wordmark and only shows the leaf glyph. */
  glyphOnly?: boolean
  /** Optional small uppercase tagline rendered after the wordmark (e.g. "POS"). */
  tagline?: string
  /** Extra class on the root element (lets callers add layout-specific styling). */
  className?: string
}

const SIZES = {
  sm: { glyph: 24, icon: 18, wordmark: 14, gap: 8 },
  md: { glyph: 32, icon: 24, wordmark: 17, gap: 10 },
  lg: { glyph: 48, icon: 36, wordmark: 26, gap: 14 },
} as const

/** The canonical NintaiGO grain glyph — verbatim from the guest favicon
 *  (`apps/dhk8-guest/public/favicon.svg`). White paths over the green glyph
 *  span; the `<rect>` background is supplied by `.dhk8-brandmark__glyph`. */
const GRAIN_GLYPH = (
  <g transform="translate(3 3) scale(0.024074)">
    <g transform="matrix(2.754321,0,0,2.754321,-1684.128161,-571.919478)">
      <path d="M874.49,421.04C874.49,421.04 906.71,366.51 976.83,367.92C976.83,367.92 941.67,425.63 874.49,421.04Z" style={{ fill: '#ffffff', opacity: 0.85, fillRule: 'nonzero' }} />
    </g>
    <g transform="matrix(2.754321,0,0,2.754321,-1684.128161,-571.919478)">
      <path d="M638.18,394.4C638.18,394.4 675.47,357.12 732.04,378.73C732.04,378.73 698.92,418.17 638.18,394.4Z" style={{ fill: '#ffffff', opacity: 0.85, fillRule: 'nonzero' }} />
    </g>
    <g transform="matrix(2.754321,0,0,2.754321,-1684.128161,-571.919478)">
      <path d="M887.79,435.14C887.79,435.14 930.02,426.75 963.07,469.13C963.07,469.13 911.85,480.39 887.79,435.14Z" style={{ fill: '#ffffff', opacity: 0.85, fillRule: 'nonzero' }} />
    </g>
    <g transform="matrix(2.754321,0,0,2.754321,-1684.128161,-571.919478)">
      <path d="M664.56,279.37C664.56,279.37 730.01,298.08 749.34,373.08C749.34,373.08 679.76,352.15 664.56,279.37Z" style={{ fill: '#ffffff', opacity: 0.85, fillRule: 'nonzero' }} />
    </g>
    <g transform="matrix(2.754321,0,0,2.754321,-1684.128161,-571.919478)">
      <path d="M775.68,313.54L806.61,313.54C811.72,313.54 815.33,318.56 813.68,323.4C810.99,331.31 807.61,342.6 806.61,351.86C805.28,364.19 804.45,376.91 805.41,386.39C805.8,390.28 802.47,393.51 798.6,392.98L760.09,387.76C756.55,387.28 754.64,383.38 756.4,380.27C758.6,376.35 761,371.02 761.76,365.36C763.06,355.68 766.44,338.67 766.98,321.96C767.13,317.26 770.98,313.54 775.68,313.54Z" style={{ fill: '#ffffff', fillRule: 'nonzero' }} />
    </g>
    <g transform="matrix(2.754321,0,0,2.754321,-1684.128161,-571.919478)">
      <path d="M836.61,313.54L869.91,313.54C874.84,313.54 878.21,318.54 876.33,323.1C871.8,334.13 865.49,354.29 865.27,381.41C865.27,381.41 865.42,391.62 866.01,399.26C866.3,403.05 863.03,406.14 859.26,405.66L821.15,400.82C816.12,400.18 815.01,395.68 817.41,391.22C821.6,383.46 825.1,370.94 826.22,360.09C827.58,346.92 827.57,332.27 827.36,322.99C827.24,317.8 831.42,313.54 836.61,313.54Z" style={{ fill: '#ffffff', fillRule: 'nonzero' }} />
    </g>
    <g transform="matrix(2.754321,0,0,2.754321,-1684.128161,-571.919478)">
      <path d="M757.24,397.03L795.9,402.76C800.43,403.43 802.88,408.46 800.58,412.41C796.45,419.49 791.61,432.69 789.23,457.01C789.23,457.01 787.83,473.7 788.52,485.46C788.74,489.26 785.34,492.26 781.59,491.6L743.56,484.93C739.5,484.22 737.41,479.66 739.54,476.12C743.12,470.16 747.51,460.09 749.61,444.49C752.39,423.91 751.81,412.52 750.97,402.94C750.66,399.4 753.73,396.5 757.24,397.02L757.24,397.03Z" style={{ fill: '#ffffff', fillRule: 'nonzero' }} />
    </g>
    <g transform="matrix(2.754321,0,0,2.754321,-1684.128161,-571.919478)">
      <path d="M819.24,409.72L856.64,415.24C860.98,415.88 863.43,420.59 861.43,424.5C857.33,432.48 851.69,447.01 848.8,470.15C848.8,470.15 846.57,487.46 848.16,501.65C848.58,505.42 845.22,508.52 841.49,507.86L804.22,501.24C799.63,500.43 797.29,495.31 799.61,491.27C803.43,484.6 808.01,473.07 810.35,454.1C812.68,435.18 812.56,423.52 811.92,416.72C811.53,412.55 815.12,409.11 819.26,409.72L819.24,409.72Z" style={{ fill: '#ffffff', fillRule: 'nonzero' }} />
    </g>
    <g transform="matrix(2.754321,0,0,2.754321,-1684.128161,-571.919478)">
      <path d="M741.57,494.53L780.08,501.74C783.77,502.43 785.69,506.54 783.85,509.81C781.69,513.67 779.21,519.51 777.37,528.02L734.6,528.02C734.6,528.02 735.83,514.86 735.22,506.02C735.08,504.02 734.93,502.26 734.78,500.74C734.41,496.92 737.8,493.82 741.58,494.53L741.57,494.53Z" style={{ fill: '#ffffff', fillRule: 'nonzero' }} />
    </g>
    <g transform="matrix(2.754321,0,0,2.754321,-1684.128161,-571.919478)">
      <path d="M802.04,510.57L840.21,517.93C843.52,518.57 845.33,522.15 843.89,525.19L842.55,528.03L795.25,528.03C795.25,528.03 795.09,522.7 794.6,517.33C794.23,513.16 797.92,509.78 802.04,510.58L802.04,510.57Z" style={{ fill: '#ffffff', fillRule: 'nonzero' }} />
    </g>
  </g>
)

export default function BrandMark({ size = 'md', glyphOnly = false, tagline, className }: BrandMarkProps) {
  const s = SIZES[size]
  return (
    <span className={className ? `dhk8-brandmark ${className}` : 'dhk8-brandmark'} aria-label="Nintai GO">
      <span
        className="dhk8-brandmark__glyph"
        style={{ width: s.glyph, height: s.glyph }}
        aria-hidden
      >
        {/* Canonical NintaiGO grain glyph (same artwork as the guest favicon). */}
        <svg width={s.icon} height={s.icon} viewBox="0 0 32 32" fill="none">
          {GRAIN_GLYPH}
        </svg>
      </span>
      {!glyphOnly && (
        <span
          className="dhk8-brandmark__wordmark"
          style={{ fontSize: s.wordmark, marginLeft: s.gap - 6 }}
        >
          Nintai <span className="dhk8-brandmark__wordmark-tail">GO</span>
          {tagline && <span className="dhk8-brandmark__tagline">{tagline}</span>}
        </span>
      )}
    </span>
  )
}
